/**
 * 权限配置文件
 * 定义各个路由的权限要求
 */

import { UserRole } from '../store/slices/authSlice';

// 路由权限配置 - 统一使用admin前缀
export const ROUTE_PERMISSIONS: Record<string, UserRole[]> = {
  // 筛选工作台 - 只有交易所和管理员可以访问
  '/admin/screening': [UserRole.EXCHANGE, UserRole.ADMIN],

  // 项目管理相关 - 发行人、交易所和管理员都可以访问
  '/admin/project-management': [
    UserRole.ISSUER,
    UserRole.EXCHANGE,
    UserRole.ADMIN,
  ],
  '/admin/project-management/*': [
    UserRole.ISSUER,
    UserRole.EXCHANGE,
    UserRole.ADMIN,
  ],
  '/admin/issuer-workbench/*': [
    UserRole.ISSUER,
    UserRole.EXCHANGE,
    UserRole.ADMIN,
  ],

  // 发行人专用页面 - 发行人、交易所和管理员都可以访问
  '/admin/issuer/*': [UserRole.ISSUER, UserRole.EXCHANGE, UserRole.ADMIN],

  // 用户管理 - 只有管理员和交易所可以访问
  '/admin/user-management': [UserRole.ADMIN, UserRole.EXCHANGE],

  // 渠道管理 - 只有管理员和交易所可以访问
  '/admin/channel-management': [UserRole.ADMIN, UserRole.EXCHANGE],

  // 管理员专用页面
  '/admin/*': [UserRole.ADMIN, UserRole.EXCHANGE, UserRole.ISSUER],
};

// 默认首页重定向配置 - 统一使用admin前缀
export const DEFAULT_ROUTES = {
  [UserRole.ADMIN]: '/admin/user-management',
  [UserRole.EXCHANGE]: '/admin/screening',
  [UserRole.ISSUER]: '/admin/issuer/questionnaires', // 发行人默认进入"我的问卷"页面
} as const;

// 权限检查函数
export const hasRoutePermission = (
  route: string,
  userRole: UserRole
): boolean => {
  // 检查精确匹配
  if (ROUTE_PERMISSIONS[route]) {
    return ROUTE_PERMISSIONS[route].includes(userRole);
  }

  // 检查通配符匹配
  for (const [pattern, allowedRoles] of Object.entries(ROUTE_PERMISSIONS)) {
    if (pattern.endsWith('/*')) {
      const basePattern = pattern.slice(0, -2);
      if (route.startsWith(basePattern)) {
        return allowedRoles.includes(userRole);
      }
    }
  }

  // 安全策略：默认拒绝访问未配置的路由
  // 只有明确配置了权限的路由才允许访问
  return false;
};

// 获取用户默认首页
export const getDefaultRoute = (userRole: UserRole): string => {
  return DEFAULT_ROUTES[userRole] || '/';
};

// API权限配置
export const API_PERMISSIONS: Record<string, UserRole[]> = {
  // 问卷相关API
  'GET /api/v1/questionnaires': [UserRole.EXCHANGE, UserRole.ADMIN],
  'POST /api/v1/questionnaires/*/review': [UserRole.EXCHANGE, UserRole.ADMIN],
  'POST /api/v1/questionnaires/batch': [UserRole.EXCHANGE, UserRole.ADMIN],

  // 发行人项目API - 发行人、交易所和管理员都可以访问
  'GET /api/v1/issuer/projects': [
    UserRole.ISSUER,
    UserRole.EXCHANGE,
    UserRole.ADMIN,
  ],
  'GET /api/v1/issuer/projects/*': [
    UserRole.ISSUER,
    UserRole.EXCHANGE,
    UserRole.ADMIN,
  ],

  // 问卷创建和提交（所有用户都可以）
  'POST /api/v1/questionnaire': [
    UserRole.ISSUER,
    UserRole.EXCHANGE,
    UserRole.ADMIN,
  ],
  'POST /api/v1/questionnaire/create': [
    UserRole.ISSUER,
    UserRole.EXCHANGE,
    UserRole.ADMIN,
  ],
  'GET /api/v1/questionnaire/*': [
    UserRole.ISSUER,
    UserRole.EXCHANGE,
    UserRole.ADMIN,
  ],
};

// API权限检查函数
export const hasApiPermission = (
  method: string,
  path: string,
  userRole: UserRole
): boolean => {
  const apiKey = `${method.toUpperCase()} ${path}`;

  // 检查精确匹配
  if (API_PERMISSIONS[apiKey]) {
    return API_PERMISSIONS[apiKey].includes(userRole);
  }

  // 检查通配符匹配
  for (const [pattern, allowedRoles] of Object.entries(API_PERMISSIONS)) {
    if (pattern.includes('*')) {
      const regex = new RegExp('^' + pattern.replace(/\*/g, '[^/]+') + '$');
      if (regex.test(apiKey)) {
        return allowedRoles.includes(userRole);
      }
    }
  }

  // 默认拒绝访问（对于没有配置的API）
  return false;
};
